/** Client-side UOM helpers (mirrors server/utils/uom.js pricing). */

/** Canonical size in smallest common unit of each family (weight → grams, volume → ml, length → mm). */
const UNIT_TO_BASE = {
  // weight → grams
  mg: 0.001,
  g: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilo: 1000,
  kilogram: 1000,
  kilograms: 1000,
  // volume → millilitres
  ml: 1,
  millilitre: 1,
  milliliter: 1,
  l: 1000,
  liter: 1000,
  litre: 1000,
  liters: 1000,
  litres: 1000,
  // length → millimetres
  mm: 1,
  cm: 10,
  m: 1000,
  meter: 1000,
  metre: 1000
};

const FRACTIONAL_UNITS = new Set(Object.keys(UNIT_TO_BASE));

const WEIGHT = new Set(['mg', 'g', 'gram', 'grams', 'kg', 'kilo', 'kilogram', 'kilograms']);
const VOLUME = new Set(['ml', 'millilitre', 'milliliter', 'l', 'liter', 'litre', 'liters', 'litres']);
const LENGTH = new Set(['mm', 'cm', 'm', 'meter', 'metre']);

export function normalizeUnitCode(code) {
  return String(code || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, '');
}

/**
 * How many baseUom units equal 1 of thisUom.
 * e.g. base=g, this=kg → 1000; base=kg, this=g → 0.001
 */
export function autoConversionToBase(thisCode, baseCode) {
  const a = normalizeUnitCode(thisCode);
  const b = normalizeUnitCode(baseCode);
  if (!a || !b) return null;
  if (a === b) return 1;
  const aFactor = UNIT_TO_BASE[a];
  const bFactor = UNIT_TO_BASE[b];
  if (aFactor == null || bFactor == null || !bFactor) return null;
  const sameFamily =
    (WEIGHT.has(a) && WEIGHT.has(b)) ||
    (VOLUME.has(a) && VOLUME.has(b)) ||
    (LENGTH.has(a) && LENGTH.has(b));
  if (!sameFamily) return null;
  return Math.round((aFactor / bFactor) * 1e9) / 1e9;
}

export function isLikelyFractionalUnit(code) {
  return FRACTIONAL_UNITS.has(normalizeUnitCode(code));
}

/** Convert qty from fromCode into toCode using known SI relationships. */
export function convertQtyBetweenUnits(qty, fromCode, toCode) {
  const q = parseFloat(qty);
  if (!Number.isFinite(q)) return null;
  const factor = autoConversionToBase(fromCode, toCode);
  if (factor == null) return null;
  return Math.round(q * factor * 1e6) / 1e6;
}

/**
 * How many inner pieces make 1 pack when conversion_to_base is 1/n (e.g. Packet in a Bdl).
 * Avoids 1/60 → 0.016667 → 59.999.
 */
export function piecesFromInnerFactor(conversionToBase) {
  const factor = parseFloat(conversionToBase);
  if (!(factor > 0) || factor >= 1) return null;
  const raw = 1 / factor;
  const nearest = Math.round(raw);
  if (nearest >= 2 && Math.abs(raw - nearest) < 0.05) return nearest;
  return Math.round(raw * 1000) / 1000;
}

export function innerFactorFromPieces(pieces) {
  const n = parseFloat(pieces);
  if (!(n > 0)) return '';
  return 1 / n;
}

export function snapInnerPackConversion(factor) {
  const pieces = piecesFromInnerFactor(factor);
  if (pieces == null || !Number.isInteger(pieces)) {
    const f = parseFloat(factor);
    return Number.isFinite(f) && f > 0 ? f : factor;
  }
  return 1 / pieces;
}

export function pickDefaultUom(uoms, role = 'sales') {
  const list = Array.isArray(uoms) ? uoms : [];
  if (!list.length) return null;
  if (role === 'sales') {
    return list.find((u) => Number(u.is_sales_default)) || list.find((u) => Number(u.is_base)) || list[0];
  }
  if (role === 'purchase') {
    return list.find((u) => Number(u.is_purchase_default)) || list.find((u) => Number(u.is_base)) || list[0];
  }
  return list.find((u) => Number(u.is_base)) || list[0];
}

export function toBaseQty(qty, conversionToBase) {
  const q = parseFloat(qty);
  const f = parseFloat(conversionToBase) || 1;
  if (!Number.isFinite(q)) return 0;
  return Math.round(q * f * 1e6) / 1e6;
}

export function fromBaseQty(qtyBase, conversionToBase) {
  const q = parseFloat(qtyBase);
  const f = parseFloat(conversionToBase) || 1;
  if (!Number.isFinite(q) || f <= 0) return 0;
  return Math.round((q / f) * 1e6) / 1e6;
}

export function effectiveSalePrice(uom, productPrice) {
  if (uom && uom.sale_price != null && uom.sale_price !== '') {
    return parseFloat(uom.sale_price) || 0;
  }
  const base = parseFloat(productPrice) || 0;
  const factor = parseFloat(uom && uom.conversion_to_base) || 1;
  return Math.round(base * factor * 1000) / 1000;
}

export function effectivePurchasePrice(uom, productCost) {
  if (uom && uom.purchase_price != null && uom.purchase_price !== '') {
    return parseFloat(uom.purchase_price) || 0;
  }
  const base = parseFloat(productCost) || 0;
  const factor = parseFloat(uom && uom.conversion_to_base) || 1;
  return Math.round(base * factor * 1000) / 1000;
}

export function stockInUom(stockBase, uom) {
  return fromBaseQty(stockBase, uom?.conversion_to_base || 1);
}

/** Count packs (Bag, Packet, pcs) sell as whole units. Weight/volume stay fractional. */
export function uomSellsFractional(uom) {
  return isLikelyFractionalUnit(uom?.code || uom?.name || uom?.uom_code);
}

/**
 * How many of this UOM can be sold from a base-unit stock amount.
 * 3.833 Bag with 6 packets/bag → 3 Bag or 23 Packet.
 */
export function maxSellableUom(qtyBase, uom) {
  const base = parseFloat(qtyBase) || 0;
  const factor = parseFloat(uom?.conversion_to_base) || 1;
  if (factor <= 0) return 0;
  if (uomSellsFractional(uom)) {
    return fromBaseQty(base, factor);
  }
  const pieces = piecesFromInnerFactor(factor);
  if (pieces != null && Number.isInteger(pieces) && pieces >= 2) {
    return Math.max(0, Math.round(base * pieces));
  }
  return Math.max(0, Math.floor(base / factor + 1e-9));
}

export function snapSellQty(qty, uom) {
  const q = parseFloat(qty);
  if (!Number.isFinite(q) || q <= 0) return 0;
  if (uomSellsFractional(uom)) return Math.round(q * 1e6) / 1e6;
  return Math.max(0, Math.round(q));
}

export function formatQty(qty) {
  const n = parseFloat(qty);
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return String(Math.round(n * 1000) / 1000);
}

/** Inner / retail unit (Packet, pcs) when the product is stocked in Box / Bundle. */
export function pickRetailUom(uoms) {
  const inner = (Array.isArray(uoms) ? uoms : []).filter((u) => {
    const factor = parseFloat(u.conversion_to_base);
    return !Number(u.is_base) && factor > 0 && factor < 1;
  });
  if (!inner.length) return null;
  const sales = inner.find((u) => Number(u.is_sales_default));
  if (sales) return sales;
  const named = inner.find((u) => /packet|pkt|pcs|pc|piece|retail|loose/i.test(String(u.code || u.name || '')));
  if (named) return named;
  return inner.slice().sort((a, b) => parseFloat(a.conversion_to_base) - parseFloat(b.conversion_to_base))[0];
}

/**
 * Stock in base unit, plus retail count when a pack unit exists.
 * 0.75 Box with 24 packets/box → "0.75 Box · 18 Packet"
 */
export function formatStockWithRetail(product) {
  const stock = parseFloat(product && product.stock_quantity) || 0;
  const baseCode = ((product && product.uoms) || []).find((u) => Number(u.is_base))?.code
    || (product && product.unit)
    || '';
  const base = `${formatQty(stock)}${baseCode ? ` ${baseCode}` : ''}`.trim();
  const retail = pickRetailUom(product && product.uoms);
  if (!retail) return base;
  const pieces = fromBaseQty(stock, retail.conversion_to_base);
  const retailCode = retail.code || retail.name || '';
  if (!retailCode) return base;
  return `${base} · ${formatQty(pieces)} ${retailCode}`;
}

/** Remaining layer qty in the same retail unit the stock column uses. */
export function formatLayerQty(product, qtyBase) {
  const qty = parseFloat(qtyBase) || 0;
  const retail = pickRetailUom(product && product.uoms);
  if (retail) {
    const code = retail.code || retail.name || '';
    return `${formatQty(fromBaseQty(qty, retail.conversion_to_base))}${code ? ` ${code}` : ''}`;
  }
  const baseCode = ((product && product.uoms) || []).find((u) => Number(u.is_base))?.code
    || (product && product.unit)
    || '';
  return `${formatQty(qty)}${baseCode ? ` ${baseCode}` : ''}`.trim();
}

/** Suggested companion units when base is set (e.g. g → kg, 500g). */
export function suggestedWeightUoms(baseCode) {
  const b = normalizeUnitCode(baseCode);
  if (b === 'g' || b === 'gram' || b === 'grams') {
    return [
      { code: 'kg', name: 'Kilogram', conversion_to_base: 1000 },
      { code: '500g', name: '500 g pack', conversion_to_base: 500 }
    ];
  }
  if (b === 'kg' || b === 'kilo' || b === 'kilogram' || b === 'kilograms') {
    return [
      { code: 'g', name: 'Gram', conversion_to_base: 0.001 },
      { code: '500g', name: '500 g pack', conversion_to_base: 0.5 }
    ];
  }
  return [];
}

export function openPriceLayers(product) {
  return (product?.price_layers || [])
    .map((layer) => ({
      id: layer.id,
      sale_price: parseFloat(layer.sale_price) || 0,
      cost_price: layer.cost_price == null || layer.cost_price === ''
        ? null
        : parseFloat(layer.cost_price) || 0,
      qty_remaining: parseFloat(layer.qty_remaining) || 0
    }))
    .filter((layer) => layer.qty_remaining > 0.0000001);
}

/** Price for one selling unit from a stock layer. Keeps a custom unit price on the current layer. */
export function layerUnitPrice(uom, layerBasePrice, catalogBasePrice) {
  const factor = parseFloat(uom?.conversion_to_base) || 1;
  const layer = parseFloat(layerBasePrice) || 0;
  const catalog = parseFloat(catalogBasePrice) || 0;
  const explicit = uom && uom.sale_price != null && uom.sale_price !== '';
  const derived = Math.round(catalog * factor * 1000) / 1000;
  if (explicit && catalog > 0 && Math.abs((parseFloat(uom.sale_price) || 0) - derived) > 0.009) {
    return Math.round(((parseFloat(uom.sale_price) || 0) * (layer / catalog)) * 1000) / 1000;
  }
  return Math.round(layer * factor * 1000) / 1000;
}

export function layerUnitCost(uom, layerBaseCost, catalogBaseCost) {
  if (layerBaseCost != null && layerBaseCost !== '') {
    const factor = parseFloat(uom?.conversion_to_base) || 1;
    return Math.round((parseFloat(layerBaseCost) || 0) * factor * 1000) / 1000;
  }
  return effectivePurchasePrice(uom, catalogBaseCost);
}

export function consumePriceLayers(layers, qtyBase) {
  let left = parseFloat(qtyBase) || 0;
  const next = (layers || []).map((layer) => ({
    ...layer,
    qty_remaining: parseFloat(layer.qty_remaining) || 0
  }));
  next.forEach((layer) => {
    if (left <= 1e-9) return;
    const take = Math.min(layer.qty_remaining, left);
    layer.qty_remaining = Math.round((layer.qty_remaining - take) * 1e6) / 1e6;
    left -= take;
  });
  return next.filter((layer) => layer.qty_remaining > 1e-6);
}
